import React, { useState, useEffect } from "react";

const FixedDepositCalculator = () => {
  const [depositAmount, setDepositAmount] = useState(100000);
  const [annualInterestRate, setAnnualInterestRate] = useState(7);
  const [tenure, setTenure] = useState(5);
  const [inflationRate, setInflationRate] = useState(6);
  const [result, setResult] = useState({
    futureValue: "0.00",
    totalInvestment: "0.00",
    estimatedReturns: "0.00",
    inflationAdjustedValue: "0.00",
  });

  // Fixed Deposit Calculation Function
  const calculateFD = () => {
    const P = parseFloat(depositAmount);
    const r = parseFloat(annualInterestRate) / 100;
    const n = parseFloat(tenure);
    const inflation = parseFloat(inflationRate) / 100;

    if (isNaN(P) || isNaN(r) || isNaN(n) || P <= 0 || n <= 0) return;

    // Compound Interest Formula for FD: A = P(1 + r)^n
    const maturityAmount = P * Math.pow(1 + r, n);
    const totalDeposit = P;
    const interestEarned = maturityAmount - totalDeposit;
    
    // Calculate inflation-adjusted value
    const inflationAdjustedValue = maturityAmount / Math.pow(1 + inflation, n);

    setResult({
      futureValue: maturityAmount.toFixed(2),
      totalInvestment: totalDeposit.toFixed(2),
      estimatedReturns: interestEarned.toFixed(2),
      inflationAdjustedValue: inflationAdjustedValue.toFixed(2),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
    calculateFD();
  }, []);

  const growthPercentage = result.totalInvestment > 0 
    ? ((Number(result.estimatedReturns) / Number(result.totalInvestment)) * 100).toFixed(1)
    : 0;

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          </div>
          <h1>Fixed Deposit Calculator</h1>
          <p>
            Calculate your FD maturity amount and interest earnings with our easy-to-use fixed deposit calculator
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                FD Details
              </h2>

              {/* Deposit Amount */}
              <div className="input-group">
                <label>Deposit Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    className="calc-input"
                    placeholder="100000"
                  />
                </div>
                <div className="quick-select-group">
                  {[50000, 100000, 500000, 1000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setDepositAmount(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? (amount / 100000).toFixed(0) + 'L' : (amount / 1000).toFixed(0) + 'K'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Annual Interest Rate */}
              <div className="input-group">
                <label>Annual Interest Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={annualInterestRate}
                    onChange={(e) => setAnnualInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="7"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[5, 6, 7, 8].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setAnnualInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Tenure */}
              <div className="input-group">
                <label>Tenure (years)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={tenure}
                    onChange={(e) => setTenure(e.target.value)}
                    className="calc-input"
                    placeholder="5"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 3, 5, 10].map((years) => (
                    <button
                      key={years}
                      onClick={() => setTenure(years)}
                      className="quick-select-btn success"
                    >
                      {years}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Inflation Rate */}
              <div className="input-group">
                <label>Expected Inflation Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(e.target.value)}
                    className="calc-input"
                    placeholder="6"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              <button onClick={calculateFD} className="calculate-btn">
                Calculate FD Returns
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                FD Summary
              </h2>

              <div className="result-card">
                {/* Total Deposit */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Deposit</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalInvestment).toLocaleString("en-IN")}
                  </div>
                </div>

                {/* Interest Earned */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Interest Earned</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.estimatedReturns).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext green">
                    +{growthPercentage}% Growth
                  </div>
                </div>

                {/* Maturity Amount - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Maturity Amount</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.futureValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After {tenure} years at {annualInterestRate}% interest
                  </div>
                </div>

                {/* Inflation Adjusted Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Inflation Adjusted Value</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.inflationAdjustedValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Real value in today's terms
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">FD Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Your Deposit</span>
                      <span className="breakdown-percentage">
                        {((Number(result.totalInvestment) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(Number(result.totalInvestment) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Interest Earned</span>
                      <span className="breakdown-percentage green">
                        {((Number(result.estimatedReturns) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(Number(result.estimatedReturns) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the Fixed Deposit Calculator Work?</h3>
              <p>
                The FD calculator estimates your maturity amount by applying compound interest to 
                your deposit. It considers the deposit amount, interest rate offered by the bank, 
                and tenure to calculate how much you'll receive at maturity. The calculator also 
                shows inflation-adjusted returns to understand the real value of your FD.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formula Used:</h4>
            <div className="formula-display">
              A = P × (1 + r)ⁿ
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">A</div>
                <div className="formula-param-value">Maturity Amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">P</div>
                <div className="formula-param-value">Deposit Amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Annual Interest Rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Tenure in Years</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is a Fixed Deposit (FD)?</h5>
              <p>
                A Fixed Deposit is a financial instrument offered by banks and NBFCs where you 
                deposit a lump sum amount for a fixed period at a predetermined interest rate. 
                FDs are considered one of the safest investment options with guaranteed returns 
                and are ideal for risk-averse investors seeking stable income.
              </p>
            </div>

            <div className="faq-item">
              <h5>How is FD interest calculated?</h5>
              <p>
                FD interest is typically calculated using compound interest, though some banks may 
                offer simple interest for certain tenures. The interest is compounded quarterly in 
                most cases, meaning the interest earned is added to the principal every quarter. 
                The final maturity amount includes both principal and accumulated interest.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is the current FD interest rate in India?</h5>
              <p>
                FD interest rates in India typically range from 5% to 8% per annum, depending on 
                the bank, tenure, and deposit amount. Senior citizens often get an additional 0.25% 
                to 0.5% interest. Rates vary between banks and change based on RBI policies and 
                market conditions. Always compare rates before investing.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I break my FD before maturity?</h5>
              <p>
                Yes, most banks allow premature withdrawal of FDs, but it usually comes with a 
                penalty of 0.5% to 1% on the interest earned. Some banks also impose a minimum 
                lock-in period. Tax-saving FDs have a mandatory 5-year lock-in and cannot be 
                withdrawn before maturity.
              </p>
            </div>

            <div className="faq-item">
              <h5>Are FD returns taxable?</h5>
              <p>
                Yes, FD interest is fully taxable as per your income tax slab. Banks deduct TDS 
                (Tax Deducted at Source) at 10% if your annual interest income exceeds ₹40,000 
                (₹50,000 for senior citizens). You can submit Form 15G/15H if your total income 
                is below the taxable limit to avoid TDS deduction.
              </p>
            </div>

            <div className="faq-item">
              <h5>Which is better: FD or other investments?</h5>
              <p>
                FDs are ideal for capital protection and guaranteed returns, making them suitable 
                for short-term goals and risk-averse investors. However, for long-term wealth 
                creation, equity mutual funds or SIPs may offer better inflation-adjusted returns. 
                A balanced portfolio should include both FDs for stability and equity for growth.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FixedDepositCalculator;